import { fetchConfig } from '@theme/utilities';
import { CartAddEvent, CartErrorEvent } from '@theme/events';

/**
 * Cart drawer upsell ("You may also like").
 *
 * - data-mode="static": cards are server-rendered; this element only handles variant selection + add to cart.
 * - data-mode="recommendations": the body is fetched from the Product Recommendations API the first time
 *   the drawer opens, and refetched whenever the cart contents change (the cart morph updates
 *   data-url / data-cart-signature on this element, while data-skip-subtree-update preserves the body).
 */
class CartUpsell extends HTMLElement {
  static observedAttributes = ['data-url', 'data-cart-signature'];

  /** @type {AbortController | null} */
  #abortController = null;
  #loaded = false;
  #stale = true;
  /** @type {HTMLElement | null} */
  #drawer = null;

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    this.addEventListener('change', this.#onChange);

    if (this.dataset.mode !== 'recommendations') return;

    this.#drawer = this.closest('cart-drawer-component');
    this.#drawer?.addEventListener('dialog:open', this.#onDrawerOpen);

    if (this.#isDrawerOpen()) this.#load();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('change', this.#onChange);
    this.#drawer?.removeEventListener('dialog:open', this.#onDrawerOpen);
    this.#abortController?.abort();
  }

  /**
   * @param {string} _name
   * @param {string | null} oldValue
   * @param {string | null} newValue
   */
  attributeChangedCallback(_name, oldValue, newValue) {
    if (this.dataset.mode !== 'recommendations' || oldValue === null || oldValue === newValue) return;

    this.#stale = true;
    if (this.#isDrawerOpen()) this.#load();
  }

  #isDrawerOpen() {
    return Boolean(this.closest('dialog')?.open);
  }

  #onDrawerOpen = () => {
    if (this.#stale || !this.#loaded) this.#load();
  };

  async #load() {
    const url = this.dataset.url;
    const body = this.querySelector('[data-upsell-body]');
    if (!url || !body) return;

    this.#abortController?.abort();
    this.#abortController = new AbortController();
    this.#stale = false;

    try {
      const response = await fetch(url, { signal: this.#abortController.signal });
      if (!response.ok) throw new Error(`Recommendations request failed: ${response.status}`);

      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const results = html.querySelector('[data-upsell-results]');

      body.innerHTML = results ? results.innerHTML : '';
      this.#loaded = true;
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      this.#stale = true;
      console.error(error);
    }
  }

  /** @param {Event} event */
  #onChange = (event) => {
    const select = event.target;
    if (!(select instanceof HTMLSelectElement) || !select.matches('[data-upsell-select]')) return;

    const card = select.closest('[data-upsell-card]');
    const option = select.selectedOptions[0];
    if (!card || !option) return;

    const addButton = card.querySelector('[data-upsell-add]');
    const price = card.querySelector('[data-upsell-price]');
    const image = card.querySelector('[data-upsell-image]');

    if (addButton instanceof HTMLButtonElement) {
      addButton.dataset.variantId = option.value;
      if (option.value && !option.disabled) {
        addButton.removeAttribute('aria-disabled');
      } else {
        addButton.setAttribute('aria-disabled', 'true');
      }
    }

    if (price && option.dataset.priceHtml) price.innerHTML = option.dataset.priceHtml;

    if (image instanceof HTMLImageElement && option.dataset.image) {
      image.removeAttribute('srcset');
      image.src = option.dataset.image;
    }

    card.classList.remove('cart-upsell-card--needs-choice');
  };

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const button = target.closest('[data-upsell-add]');
    if (!(button instanceof HTMLButtonElement) || button.disabled) return;

    const variantId = button.dataset.variantId;
    if (!variantId || button.getAttribute('aria-disabled') === 'true') {
      const card = button.closest('[data-upsell-card]');
      card?.classList.add('cart-upsell-card--needs-choice');
      const select = card?.querySelector('[data-upsell-select]');
      if (select instanceof HTMLSelectElement) select.focus();
      return;
    }

    this.#addToCart(button, variantId);
  };

  /**
   * @param {HTMLButtonElement} button
   * @param {string} variantId
   */
  async #addToCart(button, variantId) {
    const sectionIds = new Set();
    document.querySelectorAll('cart-items-component').forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId) sectionIds.add(el.dataset.sectionId);
    });

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', '1');
    if (sectionIds.size) formData.append('sections', Array.from(sectionIds).join(','));

    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    button.classList.add('is-loading');

    const fetchCfg = fetchConfig('javascript', { body: formData });

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        ...fetchCfg,
        headers: { ...fetchCfg.headers, Accept: 'text/html' },
      });
      const data = await response.json();

      if (data.status) {
        this.dispatchEvent(new CartErrorEvent('cart-upsell', data.message, data.description, data.errors));
        button.disabled = false;
        return;
      }

      // Cart drawer morphs from these sections; the card disappears because the product is now in the cart.
      this.dispatchEvent(
        new CartAddEvent({}, variantId, {
          // cart-icon.js only increments the stored count for this source; any other source overwrites it.
          source: 'product-form-component',
          itemCount: 1,
          productId: String(data.product_id ?? ''),
          sections: data.sections,
        })
      );
    } catch (error) {
      console.error(error);
      button.disabled = false;
    } finally {
      button.removeAttribute('aria-busy');
      button.classList.remove('is-loading');
    }
  }
}

if (!customElements.get('cart-upsell')) {
  customElements.define('cart-upsell', CartUpsell);
}
